/**
 * API client for Requirement Lifecycle Management.
 *
 * Talks to the backend Thread/Instance endpoints implemented in
 * api/requirements/v1/requirements.proto.
 *
 * CONTRACT NOTE: the backend uses the Kratos default protojson codec, so
 * responses are **camelCase**, proto3 int64 fields are JSON **strings**
 * (e.g. {"requirementThreadId":"8"}) and enums are their full proto names
 * (e.g. "REQUIREMENT_THREAD_STATUS_DRAFT"). Request bodies use snake_case —
 * protojson accepts both the proto field name and the json_name on input,
 * and the existing requirement-doctor client already sends snake_case, so we
 * stay consistent. The authoritative contract is api/openapi.swagger.json.
 */

import { apiCall, API_BASE_URL, type ApiError } from '../../utils/apiClient';
import {
  type ImportMode,
  type RequirementInstance,
  type RequirementThread,
  type ThreadStatus,
  instanceSourceFromEnum,
  instanceStatusFromEnum,
  threadStatusFromEnum,
  threadStatusToEnum,
} from './types';

// ---------------------------------------------------------------------------
// Wire DTOs (camelCase, int64 as string) — mapped to domain models below.
// ---------------------------------------------------------------------------

interface RequirementContentDto {
  text?: { text: string };
}

interface ThreadDto {
  requirementThreadId: string;
  projectId: string;
  name?: string;
  externalRef?: string;
  threadStatus: string;
  acceptedVersionId?: string;
  createdAt?: string;
  updatedAt?: string;
  parentThreadId?: string;
  mergedIntoThreadId?: string;
  createdBy?: string;
  submittedAt?: string;
  submittedBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  description?: string;
  tags?: string[];
  owner?: string;
  // CURRENT version's text, and the scores of the last completed analysis of
  // that version. Scores are absent — not 0 — when the version
  // was never analysed.
  content?: RequirementContentDto;
  currentVersionId?: string;
  aspiceScore?: number;
  incoseScore?: number;
  // Category assigned by that same analysis; absent when never analysed.
  category?: string;
}

interface InstanceDto {
  requirementVersionId: string;
  requirementThreadId: string;
  content?: RequirementContentDto;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  source?: string;
  versionNumber?: number;
  createdBy?: string;
  metadata?: Record<string, string>;
}

interface ListThreadsResponseDto {
  threads?: ThreadDto[];
  total?: string;
}

interface GetThreadResponseDto {
  thread: ThreadDto;
  instances?: InstanceDto[];
}

interface ListInstancesResponseDto {
  instances?: InstanceDto[];
  total?: string;
}

interface MutateInstanceResponseDto {
  instance: InstanceDto;
  supersededVersionId?: string;
}

interface ProposeRewriteResponseDto {
  instance: InstanceDto;
  replacedVersionId?: string;
}

interface ImportResultDto {
  requirementVersionId: string;
  requirementThreadId: string;
  content?: RequirementContentDto;
  status: string;
}

interface BatchResponseDto {
  results?: ImportResultDto[];
}

// ---------------------------------------------------------------------------
// DTO → domain mappers
// ---------------------------------------------------------------------------

function toThread(dto: ThreadDto): RequirementThread | null {
  const status = threadStatusFromEnum(dto.threadStatus);
  // Discard threads with an unrecognized status rather than silently treating
  // them as DRAFT (which would show edit/delete controls on unknown states).
  if (status === null) return null;
  return {
    threadId: dto.requirementThreadId,
    projectId: dto.projectId,
    name: dto.name ?? '',
    externalRef: dto.externalRef ?? '',
    status,
    acceptedVersionId: dto.acceptedVersionId ?? '0',
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    parentThreadId: dto.parentThreadId || undefined,
    mergedIntoThreadId: dto.mergedIntoThreadId || undefined,
    createdBy: dto.createdBy ?? '0',
    submittedAt: dto.submittedAt ?? '',
    submittedBy: dto.submittedBy ?? '0',
    approvedAt: dto.approvedAt ?? '',
    approvedBy: dto.approvedBy ?? '0',
    description: dto.description ?? '',
    tags: dto.tags ?? [],
    owner: dto.owner ?? '',
    text: dto.content?.text?.text ?? '',
    currentVersionId:
      dto.currentVersionId && dto.currentVersionId !== '0'
        ? dto.currentVersionId
        : undefined,
    // Left undefined rather than defaulted to 0: "never analysed" and "scored
    // 0" mean different things to the user.
    aspiceScore: dto.aspiceScore,
    incoseScore: dto.incoseScore,
    category: dto.category || undefined,
  };
}

function toInstance(dto: InstanceDto): RequirementInstance {
  return {
    versionId: dto.requirementVersionId,
    threadId: dto.requirementThreadId,
    text: dto.content?.text?.text ?? '',
    status: instanceStatusFromEnum(dto.status),
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    source: instanceSourceFromEnum(dto.source),
    versionNumber: dto.versionNumber ?? 0,
    createdBy: dto.createdBy ?? '0',
    metadata: dto.metadata ?? {},
  };
}

async function readJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Registration / import
// ---------------------------------------------------------------------------

export interface RegisterResult {
  threadId: string;
  versionId: string;
}

/**
 * Register a batch of requirement texts.
 *
 * The `mode` decides which backend endpoint is used:
 * - "draft"    → POST /requirements/batch        (AddRequirements, DRAFT threads)
 * - "approved" → POST /requirements/import/batch  (ImportRequirements, APPROVED,
 *                skips SYS2 review)
 *
 * Both share the same request body shape, so this is the only place the two
 * endpoints diverge.
 */
export async function registerRequirements(
  projectId: number,
  texts: string[],
  mode: ImportMode,
): Promise<RegisterResult[]> {
  const path =
    mode === 'approved' ? '/requirements/import/batch' : '/requirements/batch';
  const response = await apiCall(`${API_BASE_URL}${path}`, {
    method: 'POST',
    body: JSON.stringify({
      project_id: projectId,
      requirements: texts.map(text => ({ content: { text: { text } } })),
    }),
  });
  const data = await readJson<BatchResponseDto>(response);
  return (data.results ?? []).map(r => ({
    threadId: r.requirementThreadId,
    versionId: r.requirementVersionId,
  }));
}

// ---------------------------------------------------------------------------
// Thread read APIs
// ---------------------------------------------------------------------------

export interface ListThreadsResult {
  threads: RequirementThread[];
  total: number;
}

export interface ListThreadsParams {
  projectId: number;
  status?: ThreadStatus;
  limit?: number;
  offset?: number;
  /** Case-insensitive name/external_ref substring filter. */
  search?: string;
  /** Include SPLIT/MERGED terminal threads (hidden by default). */
  includeTerminal?: boolean;
  /** Only threads split from this parent thread. */
  parentThreadId?: string;
  /** Only threads merged (or proposed to be merged) into this target thread. */
  mergedIntoThreadId?: string;
}

export async function listThreads(
  params: ListThreadsParams,
): Promise<ListThreadsResult> {
  const { threads, total } = await fetchThreadsPage(params);
  return { threads, total };
}

/**
 * One ListThreads page. `rowCount` is how many rows the server sent, before
 * threads with an unrecognized status are dropped: pagination has to advance
 * by that, or the next page starts behind and repeats threads.
 */
async function fetchThreadsPage(
  params: ListThreadsParams,
): Promise<ListThreadsResult & { rowCount: number }> {
  const query = new URLSearchParams();
  query.set('projectId', String(params.projectId));
  if (params.status) {
    query.set('threadStatus', threadStatusToEnum(params.status));
  }
  if (params.limit !== undefined) {
    query.set('limit', String(params.limit));
  }
  if (params.offset !== undefined) {
    query.set('offset', String(params.offset));
  }
  if (params.search) {
    query.set('search', params.search);
  }
  if (params.includeTerminal) {
    query.set('includeTerminal', 'true');
  }
  if (params.parentThreadId) {
    query.set('parentThreadId', params.parentThreadId);
  }
  if (params.mergedIntoThreadId) {
    query.set('mergedIntoThreadId', params.mergedIntoThreadId);
  }
  const response = await apiCall(
    `${API_BASE_URL}/requirements?${query.toString()}`,
    { method: 'GET' },
  );
  const data = await readJson<ListThreadsResponseDto>(response);
  const parsedTotal = parseInt(data.total ?? '', 10);
  const rows = data.threads ?? [];
  return {
    threads: rows
      .map(toThread)
      .filter((t): t is RequirementThread => t !== null),
    total: isNaN(parsedTotal) ? 0 : parsedTotal,
    rowCount: rows.length,
  };
}

/** Matches the backend's cap on ListThreads. */
const THREAD_PAGE_SIZE = 200;
/** Stops a bad `total` from looping forever; 20 pages is 4000 threads. */
const MAX_THREAD_PAGES = 20;

/**
 * Thrown by listAllThreads when the server ignored a lineage filter, i.e. the
 * backend is older than this client. The message is developer detail; screens
 * show their own translated text.
 */
export class LineageFilterUnsupportedError extends Error {
  constructor() {
    super(
      'ListThreads ignored the lineage filter: the backend does not support it yet',
    );
    this.name = 'LineageFilterUnsupportedError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export interface ListAllThreadsResult extends ListThreadsResult {
  /** False when the page cap was hit before `total` was reached. */
  complete: boolean;
}

/**
 * Lists every thread matching `params`, following pagination to the end.
 *
 * A lineage filter the server did not apply is an error, not something to
 * filter out here: a backend older than this client ignores unknown query
 * parameters and would hand back the whole project, page after page.
 */
export async function listAllThreads(
  params: Omit<ListThreadsParams, 'limit' | 'offset'>,
): Promise<ListAllThreadsResult> {
  const threads: RequirementThread[] = [];
  let offset = 0;
  let total = Infinity;
  let page = 0;
  for (; offset < total && page < MAX_THREAD_PAGES; page++) {
    const batch = await fetchThreadsPage({
      ...params,
      limit: THREAD_PAGE_SIZE,
      offset,
    });
    if (batch.rowCount === 0) break;
    for (const t of batch.threads) {
      if (
        (params.parentThreadId && t.parentThreadId !== params.parentThreadId) ||
        (params.mergedIntoThreadId &&
          t.mergedIntoThreadId !== params.mergedIntoThreadId)
      ) {
        throw new LineageFilterUnsupportedError();
      }
    }
    threads.push(...batch.threads);
    offset += batch.rowCount;
    total = Number.isFinite(batch.total) ? batch.total : offset;
  }
  const complete = !(page === MAX_THREAD_PAGES && offset < total);
  return { threads, total: Number.isFinite(total) ? total : 0, complete };
}

/** A thread plus optionally its versions, returned by getThread. */
export interface ThreadWithInstances {
  thread: RequirementThread | null;
  instances: RequirementInstance[];
}

export async function getThread(
  threadId: string,
  opts: { includeInstances?: boolean } = {},
): Promise<ThreadWithInstances> {
  const query = opts.includeInstances ? '?includeInstances=true' : '';
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}${query}`,
    { method: 'GET' },
  );
  const data = await readJson<GetThreadResponseDto>(response);
  return {
    thread: toThread(data.thread),
    instances: (data.instances ?? []).map(toInstance),
  };
}

/**
 * Resolve several threads by id in parallel, for rendering lineage with names
 * instead of raw ids Missing/failed lookups are dropped so a
 * single deleted ancestor does not break the whole view.
 */
export async function getThreadsByIds(
  ids: string[],
): Promise<RequirementThread[]> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const settled = await Promise.allSettled(unique.map(id => getThread(id)));
  return settled
    .filter(
      (r): r is PromiseFulfilledResult<ThreadWithInstances> =>
        r.status === 'fulfilled',
    )
    .map(r => r.value.thread)
    .filter((t): t is RequirementThread => t !== null);
}

export async function updateThreadMetadata(
  threadId: string,
  patch: {
    name?: string;
    externalRef?: string;
    description?: string;
    owner?: string;
    tags?: string[];
  },
): Promise<RequirementThread | null> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) {
    body.name = patch.name;
  }
  if (patch.externalRef !== undefined) {
    body.external_ref = patch.externalRef;
  }
  if (patch.description !== undefined) {
    body.description = patch.description;
  }
  if (patch.owner !== undefined) {
    body.owner = patch.owner;
  }
  // tags_set distinguishes "replace tags" (incl. clear) from "leave unchanged".
  if (patch.tags !== undefined) {
    body.tags = patch.tags;
    body.tags_set = true;
  }
  const response = await apiCall(`${API_BASE_URL}/requirements/${threadId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  // The PATCH response echoes only the patched scalar fields, not the full thread
  // shape. Re-fetch the full thread to normalise into RequirementThread.
  const dto = await readJson<{
    requirementThreadId?: string;
    name?: string;
    externalRef?: string;
    description?: string;
    owner?: string;
    tags?: string[];
    threadStatus?: string;
  }>(response);
  const { thread } = await getThread(dto.requirementThreadId ?? threadId);
  return thread;
}

// ---------------------------------------------------------------------------
// Instance read / edit APIs
// ---------------------------------------------------------------------------

export async function listInstances(
  threadId: string,
): Promise<RequirementInstance[]> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/instances`,
    { method: 'GET' },
  );
  const data = await readJson<ListInstancesResponseDto>(response);
  return (data.instances ?? []).map(toInstance);
}

/** Save edited text as a new CURRENT version (the prior CURRENT is SUPERSEDED). */
export async function editInstance(
  threadId: string,
  text: string,
): Promise<RequirementInstance> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/instances`,
    {
      method: 'PUT',
      body: JSON.stringify({ content: { text: { text } } }),
    },
  );
  const data = await readJson<MutateInstanceResponseDto>(response);
  return toInstance(data.instance);
}

/**
 * Offer an AI rewrite for review as a PENDING_REVIEW version (CB25SES-719).
 *
 * Nothing about the requirement changes until the user decides: the thread
 * keeps its CURRENT version. From there the review APIs below take over —
 * acceptInstance promotes the proposal, rejectInstance records the refusal.
 * Proposing again retires the proposal it replaces, which is how Regenerate
 * works (CB25SES-1492); replacedVersionId reports which one went.
 */
export async function proposeRewrite(
  threadId: string,
  text: string,
): Promise<{ instance: RequirementInstance; replacedVersionId: string }> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/propose-rewrite`,
    {
      method: 'POST',
      body: JSON.stringify({ content: { text: { text } } }),
    },
  );
  const data = await readJson<ProposeRewriteResponseDto>(response);
  return {
    instance: toInstance(data.instance),
    replacedVersionId: data.replacedVersionId ?? '0',
  };
}

// ---------------------------------------------------------------------------
// Instance review APIs
// ---------------------------------------------------------------------------

async function instanceAction(
  threadId: string,
  versionId: string,
  action: 'accept' | 'reject' | 'discard',
): Promise<RequirementInstance> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/instances/${versionId}/${action}`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  const data = await readJson<MutateInstanceResponseDto>(response);
  return toInstance(data.instance);
}

export function acceptInstance(threadId: string, versionId: string) {
  return instanceAction(threadId, versionId, 'accept');
}

export function rejectInstance(threadId: string, versionId: string) {
  return instanceAction(threadId, versionId, 'reject');
}

// ---------------------------------------------------------------------------
// Thread approval APIs
// ---------------------------------------------------------------------------

export interface ApproveResult {
  threadId: string;
  approvedVersionId: string;
}

interface ApproveResponseDto {
  requirementThreadId: string;
  approvedVersionId: string;
}

async function approve(
  threadId: string,
  endpoint: 'approve' | 'force-approve',
): Promise<ApproveResult> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/${endpoint}`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  const data = await readJson<ApproveResponseDto>(response);
  return {
    threadId: data.requirementThreadId,
    approvedVersionId: data.approvedVersionId,
  };
}

export function approveThread(threadId: string) {
  return approve(threadId, 'approve');
}

// ---------------------------------------------------------------------------
// Force approve with the ASPICE gate
// ---------------------------------------------------------------------------

/** One ASPICE issue the gate raised, in the shape the analysis API uses. */
export interface ApprovalWarning {
  ruleId: string;
  violation: string;
  fix: string;
  severity: string;
  /**
   * Which requirement raised this warning. Unset on the Lifecycle screen,
   * which force-approves one thread at a time and so has nothing to
   * disambiguate. The Doctor acts on a selection, where warnings from several
   * requirements land in the same dialog and are unreadable without it.
   */
  requirementId?: string;
}

interface ForceApproveResponseDto {
  confirmed?: boolean;
  warnings?: Array<{
    ruleId?: string;
    violation?: string;
    fix?: string;
    severity?: string;
  }>;
  requirementThreadId: string;
  approvedVersionId?: string;
}

export interface ForceApproveResult {
  /** false means the gate blocked it — the thread was NOT transitioned. */
  confirmed: boolean;
  warnings: ApprovalWarning[];
  threadId: string;
  /** Only set when confirmed. */
  approvedVersionId: string | undefined;
}

/**
 * Force-approve a thread through the ASPICE fast-check gate.
 *
 * `confirmed: false` comes back as HTTP 200, not an error: the gate found
 * issues and left the thread alone. Callers must branch on it — treating the
 * call as "succeeded" makes the button look broken, because the state the UI
 * then re-reads is unchanged.
 *
 * Re-send with `confirm: true` to approve despite the warnings.
 */
export async function forceApproveThread(
  threadId: string,
  confirm = false,
): Promise<ForceApproveResult> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/force-approve`,
    { method: 'POST', body: JSON.stringify({ confirm }) },
  );
  const data = await readJson<ForceApproveResponseDto>(response);
  // An int64 that was never set serialises as the string "0", which is truthy —
  // so an unset version has to be excluded explicitly, not by truthiness.
  const approvedVersionId =
    data.approvedVersionId && data.approvedVersionId !== '0'
      ? data.approvedVersionId
      : undefined;
  return {
    // `confirmed` only exists once the ASPICE gate is deployed. Against the
    // older contract the response carries an approved version and no flag, so
    // treat that as approved — otherwise every successful force-approve would
    // raise an empty "issues found" dialog. A response with neither is treated
    // as not approved, which is the safe direction.
    confirmed: data.confirmed ?? approvedVersionId !== undefined,
    warnings: (data.warnings ?? []).map(w => ({
      ruleId: w.ruleId ?? '',
      violation: w.violation ?? '',
      fix: w.fix ?? '',
      severity: w.severity ?? '',
    })),
    threadId: data.requirementThreadId,
    approvedVersionId,
  };
}

// ---------------------------------------------------------------------------
// Thread submit (DRAFT → WIP)
// ---------------------------------------------------------------------------

/** Submit a DRAFT thread for review (DRAFT → WIP), unlocking Approve/Split/Merge. */
export async function submitThread(threadId: string): Promise<void> {
  await apiCall(`${API_BASE_URL}/requirements/${threadId}/submit`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// Thread un-approve (APPROVED → WIP)
// ---------------------------------------------------------------------------

/** Reverse a thread approval, restoring it to WIP (CB25SES-760). */
export async function unapproveThread(threadId: string): Promise<void> {
  await apiCall(`${API_BASE_URL}/requirements/${threadId}/unapprove`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// Thread soft-delete
// ---------------------------------------------------------------------------

export async function deleteThread(threadId: string): Promise<void> {
  await apiCall(`${API_BASE_URL}/requirements/${threadId}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Split
// ---------------------------------------------------------------------------

export interface SplitChildInput {
  name: string;
  text: string;
}

interface SplitResponseDto {
  parent: ThreadDto;
  children?: ThreadDto[];
}

export interface SplitResult {
  parent: RequirementThread | null;
  children: RequirementThread[];
}

/** Propose splitting a WIP thread into >= 2 child threads. */
export async function proposeSplit(
  threadId: string,
  children: SplitChildInput[],
): Promise<SplitResult> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/split`,
    {
      method: 'POST',
      body: JSON.stringify({
        children: children.map(c => ({
          name: c.name,
          content: { text: { text: c.text } },
        })),
      }),
    },
  );
  const data = await readJson<SplitResponseDto>(response);
  return {
    parent: toThread(data.parent),
    children: (data.children ?? [])
      .map(toThread)
      .filter((t): t is RequirementThread => t !== null),
  };
}

/** Accept a pending Split (parent → SPLIT, children → WIP). */
export async function acceptSplit(threadId: string): Promise<SplitResult> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/split/accept`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  const data = await readJson<SplitResponseDto>(response);
  return {
    parent: toThread(data.parent),
    children: (data.children ?? [])
      .map(toThread)
      .filter((t): t is RequirementThread => t !== null),
  };
}

/** Reject a pending Split (children soft-deleted, parent stays WIP). */
export async function rejectSplit(threadId: string): Promise<void> {
  await apiCall(`${API_BASE_URL}/requirements/${threadId}/split/reject`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

interface MergeResponseDto {
  target: ThreadDto;
  sources?: ThreadDto[];
}

export interface MergeResult {
  target: RequirementThread | null;
  sources: RequirementThread[];
}

/** Propose merging >= 2 WIP source threads into a new WIP target. */
export async function proposeMerge(params: {
  projectId: number;
  sourceThreadIds: string[];
  name: string;
  text: string;
}): Promise<MergeResult> {
  const response = await apiCall(`${API_BASE_URL}/requirements/merge`, {
    method: 'POST',
    body: JSON.stringify({
      project_id: params.projectId,
      // Send int64 ids as strings to preserve the module-wide no-precision-loss
      // invariant; protojson accepts both strings and numbers for int64.
      source_thread_ids: params.sourceThreadIds,
      name: params.name,
      content: { text: { text: params.text } },
    }),
  });
  const data = await readJson<MergeResponseDto>(response);
  return {
    target: toThread(data.target),
    sources: (data.sources ?? [])
      .map(toThread)
      .filter((t): t is RequirementThread => t !== null),
  };
}

/** Accept a pending Merge (sources → MERGED, target stays WIP). */
export async function acceptMerge(
  targetThreadId: string,
): Promise<MergeResult> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${targetThreadId}/merge/accept`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  const data = await readJson<MergeResponseDto>(response);
  return {
    target: toThread(data.target),
    sources: (data.sources ?? [])
      .map(toThread)
      .filter((t): t is RequirementThread => t !== null),
  };
}

/** Reject a pending Merge (sources revert to WIP, target soft-deleted). */
export async function rejectMerge(targetThreadId: string): Promise<void> {
  await apiCall(`${API_BASE_URL}/requirements/${targetThreadId}/merge/reject`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export type { ApiError };
